import React, { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Video, Download, Hash, Calendar, User, RefreshCw, Award } from 'lucide-react';
import { toast } from 'sonner';
import ReconfirmationIndicator from './ReconfirmationIndicator';

/**
 * VideoHistory Component
 * 
 * Displays all video confirmations in chronological order
 */
export default function VideoHistory({ agreement, onRefresh }) {
  const { api, token } = useAuth();
  const { t, getTemplateLabel, getTemplateName } = useLanguage();
  const [videos, setVideos] = useState([]);
  const [clarifications, setClarifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!agreement?.id) return;
    loadVideoHistory();
  }, [agreement?.id]);

  const loadVideoHistory = async () => {
    setLoading(true);
    try {
      // Load video confirmations
      const videosRes = await api.get(`/loans/${agreement.id}/video-confirmations`);
      const videosList = videosRes.data || [];

      // Sort by created_at chronologically (oldest first)
      videosList.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setVideos(videosList);

      // Load clarification requests
      try {
        const clarRes = await api.get(`/loans/${agreement.id}/clarification-requests`);
        setClarifications(clarRes.data || []);
      } catch (err) {
        // Clarification endpoint might not exist yet, ignore error
        setClarifications([]);
      }
    } catch (err) {
      toast.error(t('errors.server_error'));
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getOriginalVideo = (videoId) => {
    return videos.find(v => v.id === videoId);
  };

  const getClarificationForVideo = (videoId) => {
    return clarifications.find(c => c.reconfirmation_video_id === videoId);
  };

  const getRoleLabel = (video) => {
    const templateType = agreement.agreement_template || 'LOAN';
    if (video.user_id === agreement.created_by_user_id) {
      return getTemplateLabel(templateType, 'principal');
    }
    return getTemplateLabel(templateType, 'secondary');
  };

  const handleDownloadVideo = async (video) => {
    try {
      const res = await api.get(`/files/${video.storage_path}`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = video.file_name;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error(t('video.record.error') || 'Video download failed');
    }
  };

  const handleDownloadCertificate = (vidId) => {
    const endpointPrefix = agreement.agreement_template ? 'agreements' : 'loans';
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/${endpointPrefix}/${agreement.id}/video-confirmations/${vidId}/certificate?auth=${token}`, '_blank');
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (videos.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-muted-foreground" />
            {t('video.list.title')}
          </CardTitle>
          <CardDescription>
            {t('video.list.no_videos')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <Video className="w-12 h-12 mx-auto mb-3 opacity-20" />
            <p className="text-sm">{t('video.list.no_videos')}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Video className="w-5 h-5 text-primary" />
              {t('video.list.title')}
            </CardTitle>
            <CardDescription>
              {t('video.list.complete_record') || 'Complete record of all video confirmations'}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={loadVideoHistory}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            {t('common.refresh') || 'Refresh'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {videos.map((video, index) => {
            const originalVideo = video.reconfirmation_of ? getOriginalVideo(video.reconfirmation_of) : null;
            const clarification = video.reconfirmation_of ? getClarificationForVideo(video.id) : null;

            return (
              <div
                key={video.id}
                className="border rounded-sm p-4 space-y-4 hover:bg-muted/30 transition-colors"
              >
                {/* Video Header */}
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">
                        {t('video.record.title')} #{index + 1}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span>{getRoleLabel(video)}</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleDownloadVideo(video)}
                      className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                    >
                      <Download className="w-3 h-3" />
                      {t('document.list.download')}
                    </button>
                    <button
                      onClick={() => handleDownloadCertificate(video.id)}
                      className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/80 font-medium ml-2"
                    >
                      <Award className="w-3 h-3" />
                      {t('video.list.download_certificate')}
                    </button>
                  </div>
                </div>

                {/* Reconfirmation Indicator */}
                {video.reconfirmation_of && (
                  <ReconfirmationIndicator
                    video={video}
                    originalVideo={originalVideo}
                    clarificationRequest={clarification}
                    agreementId={agreement.id}
                  />
                )}

                {/* Video Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Calendar className="w-3 h-3" />
                      <span>{t('video.list.recorded_on')}</span>
                    </div>
                    <p className="font-medium">
                      {new Date(video.created_at).toLocaleString()}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <User className="w-3 h-3" />
                      <span>{t('video.list.recorded_by')}</span>
                    </div>
                    <p className="font-medium">{video.user_name}</p>
                  </div>
                </div>

                {/* Video Hash */}
                {video.file_hash && (
                  <div className="bg-muted/50 rounded-sm p-3 space-y-1">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Hash className="w-3 h-3" />
                      <span>{t('video.list.hash') || 'SHA-256 Hash'}</span>
                    </div>
                    <p className="font-mono text-xs break-all text-foreground">
                      {video.file_hash}
                    </p>
                  </div>
                )}

                {/* Identity Statement */}
                {video.identity_statement && (
                  <div className="border-l-4 border-primary pl-3 py-2">
                    <p className="text-xs text-muted-foreground mb-1">{t('video.record.identity_statement')}</p>
                    <p className="text-xs italic">"{video.identity_statement}"</p>
                  </div>
                )}

                {/* Consent Information */}
                {video.consent_given && (
                  <div className="text-xs text-muted-foreground">
                    {t('video.record.consent_checkbox')} &middot; {new Date(video.consent_timestamp).toLocaleString()}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
